import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PenTool, Layers, Image as ImageIcon, Instagram, Youtube, Fingerprint, Clapperboard, LayoutGrid } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { GALLERY_CATEGORIES, scrollToId } from "@/lib/site";

const CAT_ICONS = {
  "Graphic Design": PenTool,
  "Photoshop Artwork": Layers,
  Posters: ImageIcon,
  "Social Media Designs": Instagram,
  Thumbnails: Youtube,
  Branding: Fingerprint,
  "Video Projects": Clapperboard,
};

const TILES = [
  { cat: "Graphic Design", style: "amber", h: "h-64" },
  { cat: "Video Projects", style: "dark", h: "h-72" },
  { cat: "Photoshop Artwork", style: "glass", h: "h-60" },
  { cat: "Posters", style: "glass", h: "h-80" },
  { cat: "Branding", style: "dark", h: "h-56" },
  { cat: "Social Media Designs", style: "amber", h: "h-64" },
  { cat: "Thumbnails", style: "glass", h: "h-72" },
  { cat: "Graphic Design", style: "glass", h: "h-56" },
  { cat: "Video Projects", style: "amber", h: "h-64" },
  { cat: "Photoshop Artwork", style: "dark", h: "h-72" },
  { cat: "Posters", style: "amber", h: "h-60" },
  { cat: "Branding", style: "glass", h: "h-64" },
];

const STYLES = {
  amber: "bg-gradient-to-br from-amber-500 via-brand to-amber-600 text-obsidian",
  dark: "bg-obsidian-elevated border border-amber-500/20 text-white",
  glass: "bg-white/[0.04] border border-white/10 text-white",
};

function Tile({ tile, index }) {
  const Icon = CAT_ICONS[tile.cat] || ImageIcon;
  const isAmber = tile.style === "amber";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative mb-6 break-inside-avoid overflow-hidden rounded-3xl ${STYLES[tile.style]} ${tile.h}`}
      data-testid={`gallery-tile-${index}`}
    >
      <div
        className={`absolute inset-0 grid-lines transition-transform duration-700 group-hover:scale-110 ${isAmber ? "opacity-[0.12] invert" : "opacity-100"}`}
      />
      <div className="relative h-full flex flex-col justify-between p-6">
        <div className="flex items-start justify-between">
          <span
            className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
              isAmber ? "bg-obsidian/15" : "bg-amber-500/10 border border-amber-500/30 text-amber-500"
            }`}
          >
            <Icon className="h-5 w-5" />
          </span>
          <span className={`font-display text-4xl font-extrabold select-none ${isAmber ? "text-obsidian/25" : "text-stroke opacity-70"}`}>
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div>
          <p className={`font-display text-base font-semibold leading-snug ${isAmber ? "text-obsidian" : "text-white"}`}>
            {tile.cat}
          </p>
          <p className={`mt-1 font-mono text-[10px] uppercase tracking-[0.22em] ${isAmber ? "text-obsidian/60" : "text-gray-500"}`}>
            Student Work · Placeholder
          </p>
        </div>
      </div>
      <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-amber-500 to-brand transition-transform duration-500 group-hover:scale-x-100" />
    </motion.div>
  );
}

export default function StudentWork() {
  const [filter, setFilter] = useState("All Work");
  const visible = TILES.filter((t) => filter === "All Work" || t.cat === filter);

  return (
    <section id="student-work" className="relative py-20 md:py-28" data-testid="student-work-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeading
            eyebrow="Student Work"
            title="Made by Our Students"
            sub="Learning becomes powerful when you create something of your own."
          />
          <Reveal delay={0.2}>
            <button
              onClick={() => document.querySelector("#student-work .masonry")?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
              className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-6 py-3 text-sm font-medium text-amber-400 hover:bg-amber-500 hover:text-obsidian transition-all duration-300"
              data-testid="view-student-work-button"
            >
              <LayoutGrid className="h-4 w-4" />
              View Student Work
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="gallery-filters">
            {["All Work", ...GALLERY_CATEGORIES].map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] transition-all duration-300 ${
                  filter === cat
                    ? "bg-amber-500 text-obsidian font-semibold shadow-glow-amber-sm"
                    : "border border-white/12 text-gray-400 hover:border-amber-500/50 hover:text-amber-400"
                }`}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="masonry mt-10 columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="gallery-masonry">
          <AnimatePresence mode="popLayout">
            {visible.map((tile, i) => (
              <Tile key={`${tile.cat}-${i}`} tile={tile} index={i} />
            ))}
          </AnimatePresence>
        </div>

        <Reveal>
          <p className="mt-8 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500" data-testid="gallery-placeholder-note">
            Placeholder previews — real student projects will be featured here soon
          </p>
        </Reveal>
      </div>
    </section>
  );
}
